import React from 'react';
import Button from '../../../components/ui/Button';

const StatusChangeModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  newStatus,
  userName
}) => {
  if (!isOpen) return null;

  const statusInfo = {
    active: {
      label: 'Active',
      color: 'text-green-600',
      bg: 'bg-green-50 border-green-200'
    },
    inactive: {
      label: 'Inactive',
      color: 'text-red-600',
      bg: 'bg-red-50 border-red-200'
    }
  };

  const currentInfo = statusInfo?.[currentStatus] || statusInfo?.active;
  const newInfo = statusInfo?.[newStatus] || statusInfo?.active;

  const getImpactMessages = () => {
    if (newStatus === 'inactive' && currentStatus === 'active') {
      return [
        'User will be immediately logged out of all sessions',
        'User cannot access the system until reactivated',
        'All scheduled tasks for this user will be paused',
        'User will not receive system notifications'
      ];
    } else if (newStatus === 'active' && currentStatus === 'inactive') {
      return [
        'User will regain access to the system',
        'Previous permissions and settings will be restored',
        'User will receive a notification about account reactivation',
        'Scheduled tasks will resume normal operation'
      ];
    }
    return [];
  };

  const impactMessages = getImpactMessages();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-lg p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:my-16">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Confirm Status Change
            </h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* User and Status Change Summary */}
            <div className="bg-muted/20 rounded-lg p-4">
              <p className="text-sm text-foreground mb-3">
                <span className="font-medium">User:</span> {userName}
              </p>
              <div className="flex items-center space-x-3 text-sm">
                <span className="font-medium">Status:</span>
                <span className={currentInfo?.color}>
                  {currentInfo?.label}
                </span>
                <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <span className={newInfo?.color}>
                  {newInfo?.label}
                </span>
              </div>
            </div>

            {/* Impact Summary */}
            {impactMessages?.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-foreground">Impact of This Change</h4>
                
                <div className={newStatus === 'inactive' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}>
                  <div className="rounded-lg p-3">
                    <ul className="space-y-2">
                      {impactMessages?.map((message, index) => (
                        <li key={index} className={`text-sm flex items-start ${newStatus === 'inactive' ? 'text-red-700' : 'text-green-700'}`}>
                          <svg 
                            className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${newStatus === 'inactive' ? 'text-red-600' : 'text-green-600'}`} 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            {newStatus === 'inactive' ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            )}
                          </svg>
                          {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Warning for Deactivation */}
            {newStatus === 'inactive' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Account Deactivation</p>
                    <p className="text-xs">
                      This action will immediately suspend the user's access. The account can be reactivated 
                      later without data loss. All user data and settings will be preserved.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Note</p>
                  <p className="text-xs">
                    This change takes effect immediately and will be logged for audit purposes. 
                    {newStatus === 'inactive' ? ' The user will be notified of the account suspension.' : ' The user will be notified of the account reactivation.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              variant={newStatus === 'inactive' ? 'destructive' : 'default'}
              iconName="Check"
              iconPosition="left"
            >
              {newStatus === 'inactive' ? 'Deactivate Account' : 'Activate Account'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;